import { type ReactNode } from 'react';
import { trans } from '@lingoda/i18n';
import { useResolution } from '../../../hooks';
import { Box } from '../../../layout/Box';
import { Grid } from '../../../layout/GridNext';
import { Button } from '../../../buttons';
import type { GridProps } from '../../../layout/GridNext';

export type ConfirmStatus = 'enabled' | 'disabled' | 'loading';
export type ConfirmVariant = 'primary' | 'alert';

export type DismissVariant = 'text' | 'outlined';

export interface ConfirmButtonProps {
    dataCy: string;
    onConfirm: () => void;
}

export interface Props {
    confirmLabel?: string;
    dismissLabel?: string;
    confirmStatus?: ConfirmStatus;
    confirmVariant?: ConfirmVariant;
    confirmIcon?: ReactNode;
    dismissVariant?: DismissVariant;
    renderConfirmButton?: (props: ConfirmButtonProps) => ReactNode;
    onConfirm: () => void;
    onDismiss?: () => void;
    layoutVariant?: 'end-auto' | 'start-auto' | 'center-auto' | 'center-stretch';
}

const DialogActions = ({
    confirmLabel = trans('btn-ok', {}, 'public-common'),
    dismissLabel = trans('btn-close', {}, 'public-common'),
    confirmStatus,
    confirmVariant = 'primary',
    confirmIcon,
    dismissVariant = 'text',
    renderConfirmButton,
    onConfirm,
    onDismiss,
    layoutVariant = 'end-auto',
}: Props) => {
    const isMobile = useResolution('phone');

    const confirmButton = renderConfirmButton?.({ dataCy: 'dialog-confirm-btn', onConfirm });
    const { gridContainerProps, gridItemProps } = mapLayoutProps(layoutVariant);

    return (
        <Box pt={2} pb={{ xs: 3, sm: 4 }} px={{ xs: 3, sm: 4 }}>
            <Grid container spacing={isMobile ? 2 : 3} {...gridContainerProps}>
                <Grid order={{ sm: 1 }} {...gridItemProps}>
                    {confirmButton || (
                        <Button
                            variant="contained"
                            color={confirmVariant === 'alert' ? 'error' : 'primary'}
                            startIcon={confirmIcon}
                            data-cy="dialog-confirm-btn"
                            disabled={confirmStatus === 'disabled'}
                            fullWidth
                            onClick={onConfirm}
                            loading={confirmStatus === 'loading'}
                        >
                            {confirmLabel}
                        </Button>
                    )}
                </Grid>
                {onDismiss && (
                    <Grid {...gridItemProps}>
                        <Button
                            variant={dismissVariant}
                            data-cy="dialog-dismiss-btn"
                            color="primary"
                            fullWidth
                            onClick={onDismiss}
                            disabled={confirmStatus === 'loading'}
                        >
                            {dismissLabel}
                        </Button>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

function mapLayoutProps(
    layoutVariant: 'center-auto' | 'center-stretch' | 'start-auto' | 'end-auto',
): Partial<{ gridContainerProps: GridProps; gridItemProps: GridProps }> {
    switch (layoutVariant) {
        case 'center-auto':
            return {
                gridContainerProps: { justifyContent: 'center' },
                gridItemProps: { xs: 12, sm: 'auto' },
            };
        case 'center-stretch':
            return {
                gridContainerProps: { justifyContent: 'center', alignItems: 'stretch' },
                gridItemProps: { xs: 12, sm: 'auto', flexGrow: { sm: 1 } },
            };
        case 'start-auto':
            return {
                gridContainerProps: { justifyContent: 'flex-start' },
                gridItemProps: { xs: 12, sm: 'auto' },
            };
        case 'end-auto':
            return {
                gridContainerProps: { justifyContent: 'flex-end' },
                gridItemProps: { xs: 12, sm: 'auto' },
            };
    }
}

export default DialogActions;
